/**
 * useSSE — opens a Server-Sent Events stream to the backend and dispatches
 * each event to a handler map.
 *
 * Why a hook (vs putting EventSource in Context):
 *  - One connection per tab is enforced via React's effect lifecycle
 *  - Auto-pause on backgrounded tab → reconnects on visibility
 *  - Exponential backoff with jitter on failure
 *  - EventSource fires `error` for ANY disconnect (including normal close);
 *    we treat error → retry but with the backoff hat on
 *
 * Auth: cookies on EventSource — set `withCredentials: true`.  Backend uses
 * the standard `/api` cookie-or-Bearer auth.
 */
import { useEffect, useRef } from 'react';
import usePageVisibility from '../hooks/usePageVisibility';

const MAX_BACKOFF = 30000;   // 30s cap
const BASE_BACKOFF = 1000;   // start at 1s
const JITTER = 500;          // ±500ms

export default function useSSE(url, handlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const visible = usePageVisibility();

  useEffect(() => {
    if (!url || !visible || typeof EventSource === 'undefined') return;
    let es = null;
    let retries = 0;
    let reconnectTimer = null;
    let stopped = false;

    const open = () => {
      if (stopped) return;
      try {
        es = new EventSource(url, { withCredentials: true });
      } catch (_) {
        scheduleReconnect();
        return;
      }
      es.onopen = () => {
        retries = 0;
        handlersRef.current?.onOpen?.();
      };
      es.onerror = () => {
        // EventSource auto-retries on transient errors, but we want a
        // bounded backoff and to surface "disconnected" to handlers.
        handlersRef.current?.onError?.();
        try { es?.close(); } catch (_) { /* noop */ }
        es = null;
        scheduleReconnect();
      };
      // Named event handlers — backend sends "event: notification\ndata: ..."
      const types = Object.keys(handlersRef.current || {});
      for (const k of types) {
        if (k === 'onOpen' || k === 'onError') continue;
        es.addEventListener(k, (e) => {
          try {
            const payload = e.data ? JSON.parse(e.data) : {};
            handlersRef.current[k]?.(payload);
          } catch (_) { /* swallow malformed */ }
        });
      }
    };

    const scheduleReconnect = () => {
      if (stopped) return;
      retries += 1;
      const wait = Math.min(MAX_BACKOFF, BASE_BACKOFF * 2 ** Math.min(retries, 5))
        + (Math.random() * JITTER * 2 - JITTER);
      reconnectTimer = setTimeout(open, wait);
    };

    open();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { es?.close(); } catch (_) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, visible]);
}
